"use client";

import { useEffect, useRef } from "react";
import { ensureError, normalizeErrorMetadata } from "@project-x/api-clients";
import { appLogger } from "./logger";

type ObservabilityContext = Record<string, unknown>;

type SentryLike = {
  captureException?: (error: unknown, context?: { extra?: Record<string, unknown> }) => void;
  captureEvent?: (event: { message?: string; level?: string; extra?: Record<string, unknown> }) => void;
};

declare global {
  interface Window {
    Sentry?: SentryLike;
    __SENTRY__?: SentryLike;
  }
}

const getSentry = (): SentryLike | undefined => {
  if (typeof window === "undefined") {
    return undefined;
  }
  return window.Sentry ?? window.__SENTRY__;
};

export const reportClientException = (
  error: unknown,
  context: ObservabilityContext = {},
): void => {
  const normalized = normalizeErrorMetadata(error);
  const metadata: Record<string, unknown> = {
    ...context,
    error: normalized,
  };

  appLogger.error?.("ui.exception", metadata);

  const sentry = getSentry();
  if (sentry?.captureException) {
    sentry.captureException(ensureError(error), { extra: metadata });
  } else if (sentry?.captureEvent) {
    sentry.captureEvent({
      message: normalized.message,
      level: "error",
      extra: metadata,
    });
  }
};

export const useExceptionLogger = (
  error: Error | null | undefined,
  context?: ObservabilityContext,
): void => {
  const contextRef = useRef<ObservabilityContext | undefined>(context);

  useEffect(() => {
    contextRef.current = context;
  }, [context]);

  useEffect(() => {
    if (!error) {
      return;
    }
    reportClientException(error, contextRef.current ?? {});
  }, [error]);
};
